import { useId, useState, type FormEvent } from 'react';
import { Dialog } from '@240tutoring/labs-ui/dialog';
import type { ScheduledUnit } from '../../core/schedule';
import type { IsoDate, Plan } from '../../core/types';
import { weekOf } from '../../core/calendar';
import { dateRange } from './UnitRow';

/**
 * R14: a plan built after the first day asks which unit the class is on now. It asks again
 * when the current unit is cleared on a built plan (R17). Pure, so the rule is testable.
 */
export function shouldAskCurrentUnit({ plan, fresh, builtAt, today }: {
  plan: Pick<Plan, 'units' | 'currentUnitId' | 'calendar'>; fresh: boolean; builtAt: string | null; today: IsoDate;
}): boolean {
  return plan.units.length > 0 && plan.currentUnitId === null && today > plan.calendar.firstDay && (fresh || builtAt !== null);
}

/**
 * Whether the picker is open: the teacher asked for it, or it is due and was not dismissed.
 * Closing it, by picking or by "Not now", dismisses it until `askAgain`.
 */
export function useCurrentUnitAsk() {
  const [picking, setPicking] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  return {
    asking: (due: boolean) => picking || (!dismissed && due),
    open: () => setPicking(true),
    close: () => { setPicking(false); setDismissed(true); },
    askAgain: () => setDismissed(false),
  };
}

/** Pick the current unit (R14, R17). Defaults to the current unit, else the first one running this week or later. */
export function CurrentUnitPicker({ units, currentUnitId, today, busy = false, onPick, onClose }: {
  units: ScheduledUnit[]; currentUnitId: string | null; today: IsoDate; busy?: boolean; onPick(unitId: string): void; onClose(): void;
}) {
  const titleId = useId();
  const week = weekOf(today);
  // In a break week no unit covers this week, so the next one to run is the likely answer.
  const planned = units.find(u => u.weekStarts.some(w => w >= week)) ?? units.at(-1);
  const [chosen, setChosen] = useState(currentUnitId ?? planned?.id ?? '');
  const submit = (event: FormEvent) => { event.preventDefault(); if (chosen) onPick(chosen); };
  return <Dialog titleId={titleId} onClose={onClose} className="picker-dialog">
    <form onSubmit={submit}>
      <h2 id={titleId}>Which unit is your class on now?</h2>
      <p className="muted">That unit covers this week. The units before it show as done, and the rest follow on.</p>
      <fieldset className="picker-options">
        <legend className="sr-only">Units</legend>
        {units.map(u => <label key={u.id} className={`source-option${chosen === u.id ? ' selected' : ''}`}>
          <input type="radio" name="current-unit" value={u.id} checked={chosen === u.id} onChange={() => setChosen(u.id)} />
          <span><strong>{u.name}</strong><small>{dateRange(u)}</small></span>
        </label>)}
      </fieldset>
      <div className="dialog-actions">
        <button className="secondary" type="button" onClick={onClose} disabled={busy}>Not now</button>
        <button className="primary" type="submit" disabled={busy || !chosen}>Set current unit</button>
      </div>
    </form>
  </Dialog>;
}
